import axios from 'axios';
import env from './env';
import { acquireMfaToken, getBearerToken, hasEntraAccount, invalidateEntraAccessToken } from '../auth/runtimeAuth';
import { tokenForRequest } from '../auth/requestAuth';
import { endInvalidSession, isSessionInvalidError, sessionInvalidReason } from '../auth/sessionInvalid';

const API = axios.create({
  baseURL: env.API_BASE_URL
});

// Store original methods before overriding
const originalMethods = {};
['get', 'post', 'put', 'patch', 'delete'].forEach(method => {
  originalMethods[method] = API[method].bind(API);
});

// Enhanced request method with error handling options
const createRequestMethod = (method) => {
  return function(url, dataOrConfig, config = {}) {
    // Handle different parameter patterns for different HTTP methods
    let actualConfig = config;
    let data = dataOrConfig;
    
    if (method === 'get' || method === 'delete') {
      actualConfig = dataOrConfig || {};
      data = undefined;
    }
    
    // Extract handlingErrorLocally from config, default to false
    const { handlingErrorLocally = false, ...restConfig } = actualConfig;
    
    // Store the flag in the config for the interceptor to use
    restConfig._handlingErrorLocally = handlingErrorLocally;
    
    // Call the original axios method using stored reference
    const args = data !== undefined ? [url, data, restConfig] : [url, restConfig];
    return originalMethods[method](...args);
  };
};

// Override axios methods with enhanced versions
['get', 'post', 'put', 'patch', 'delete'].forEach(method => {
  API[method] = createRequestMethod(method);
});

API.interceptors.request.use(
  async function (config) {
    const token = await tokenForRequest(config.url, getBearerToken);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

// Add a response interceptor
API.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    // The server wants two-step verification first: ask Microsoft for it, then
    // retry once. While Microsoft shows its code page the request never settles,
    // since the page is leaving anyway.
    if (
      error.response?.status === 401
      && error.response?.data?.error?.code === 'mfa_required'
      && !error.config?._mfaRetried
      && !localStorage.getItem('token')
      && hasEntraAccount()
    ) {
      return acquireMfaToken().then(
        token => (token ? API.request({ ...error.config, _mfaRetried: true }) : new Promise(() => {})),
        // Microsoft could not supply it (MfaUnavailableError): surface the 401.
        () => Promise.reject(error),
      );
    }
    // The stored session is dead for good (e.g. JWT_SECRET was rotated): sign out
    // quietly. The route guards then show the login page, with no error toast.
    const sessionInvalid = isSessionInvalidError(error);
    if (sessionInvalid) {
      endInvalidSession(localStorage, sessionInvalidReason(error));
    } else if (error.response?.status === 401 && !localStorage.getItem('token') && hasEntraAccount()) {
      invalidateEntraAccessToken();
    }
    // Check if error should be handled locally
    const handlingErrorLocally = error.config?._handlingErrorLocally || false;
        
    // Exclude login errors from notification system
    if (error.config?.url?.includes('/login')) {
      return Promise.reject(error);
    }

    // Create an error object to be used by the floating error
    const errorObject = {
      code: error.response?.status,
      message: error.response?.data?.error?.message,
      hebMessage: error.response?.data?.error?.hebMessage,
    };

    // Show floating error only if not handled locally
    if (!handlingErrorLocally && !sessionInvalid && window.showFloatingError && errorObject.hebMessage) {
      window.showFloatingError(errorObject);
    }

    // Enhance the error object for easier access
    const enhancedError = {
      ...error,
      message: errorObject.hebMessage || errorObject.message || error.message,
      serverError: errorObject
    };
    
    // Always throw an exception
    return Promise.reject(enhancedError);
  }
);

export default API;